/* eslint-disable no-console */
import { updateAccountIconStatus, updateAccountStatusMessage } from './account-status-helpers';

const baseUrl = '';
// Local Web API controller that replaces the external digital user REST service.
const apiBaseUrl = '/api/digitaluser';

// Small plain-JavaScript XHR helper used for every call to the ApiController.
const ajax = ({
  method,
  url,
  contentType = 'application/json',
  accept,
  body = null,
  parse = true,
  onSuccess,
  onError,
}) => {
  const request = new XMLHttpRequest();
  request.open(method, url, true);
  if (contentType) {
    request.setRequestHeader('Content-type', contentType);
  }
  if (accept) {
    request.setRequestHeader('Accept', accept);
  }
  request.onload = () => {
    if (request.status >= 200 && request.status < 400) {
      if (onSuccess) {
        onSuccess(parse ? JSON.parse(request.responseText) : request.responseText);
      }
    } else if (onError) {
      onError(request);
    }
  };
  request.onerror = () => {
    if (onError) {
      onError(request);
    }
  };
  request.send(body);
};

const addVisualization = (data, callback) => {
  // The logged-in user id is resolved from the server-side session in the ApiController.
  ajax({
    method: 'POST',
    url: `${apiBaseUrl}/visualization`,
    contentType: 'text/plain',
    accept: 'application/json',
    body: data.toString(),
    parse: false,
    onSuccess: () => {
      if (callback) {
        callback();
      }
      updateAccountIconStatus();
      updateAccountStatusMessage('visualizer scene', 'add');
    },
    onError: () => {
      console.log('error');
    },
  });
};

const addDealer = (data, callback) => {
  ajax({
    method: 'POST',
    url: `${apiBaseUrl}/dealer`,
    body: JSON.stringify({
      'record-id': data['record-id'],
      'ship-to-account': data['ship-to-account'],
    }),
    onSuccess: (responseData) => {
      if (callback) {
        callback(responseData);
      }
      updateAccountIconStatus();
      updateAccountStatusMessage('dealer', 'add');
    },
    onError: () => {
      console.log('error');
    },
  });
};

const addSwatch = (data, callback) => {
  const {
    productLineCode,
    colorNumber,
    swatchId,
  } = data;
  ajax({
    method: 'POST',
    url: `${apiBaseUrl}/swatch`,
    body: JSON.stringify({
      'product-line-code': productLineCode,
      'color-number': colorNumber,
      'swatch-id': swatchId,
    }),
    onSuccess: (responseData) => {
      if (callback) {
        callback(responseData);
      }
      updateAccountIconStatus();
      updateAccountStatusMessage('swatch', 'add');
    },
    onError: () => {
      console.log('error');
    },
  });
};

const addSwatches = (data, callbacks) => {
  const swatchlist = data.map(swatch => ({
    'user-id': window.userId,
    'product-line-code': swatch.productLineCode,
    'color-number': swatch.colorNumber,
    'swatch-id': swatch.swatchId,
    'widen-url': swatch.widenUrl,
  }));
  ajax({
    method: 'POST',
    url: `${baseUrl}/addswatches`,
    body: JSON.stringify({
      data: JSON.stringify({
        swatchlist,
      }),
    }),
    onSuccess: (responseData) => {
      if (callbacks && callbacks.onSuccess) {
        callbacks.onSuccess(responseData);
      }
    },
    onError: (request) => {
      if (callbacks && callbacks.onError) {
        callbacks.onError(JSON.parse(request.responseText));
      }
    },
  });
};

const getItemsByType = (type, callback) => {
  let requestUrl;
  switch (type) {
    case 'location':
      requestUrl = `${apiBaseUrl}/dealer/brand/${window.brandId}`;
      break;
    case 'swatch':
      requestUrl = `${apiBaseUrl}/swatches/microsite/${window.micrositeName}`;
      break;
    case 'visualization':
      requestUrl = `${apiBaseUrl}/visualizations`;
      break;
    default:
      break;
  }
  ajax({
    method: 'GET',
    url: requestUrl,
    onSuccess: (responseData) => {
      if (callback) {
        callback(responseData);
      }
    },
    onError: () => {
      console.log('error');
    },
  });
};

const getAccountData = (callback) => {
  ajax({
    method: 'GET',
    url: `${apiBaseUrl}/account`,
    onSuccess: (responseData) => {
      if (callback) {
        callback(responseData);
      }
    },
    onError: () => {
      console.log('error');
    },
  });
};

// Posts JSON to the LoginPage URL, whose controller (LoginPageController.Index, HttpPost)
// model-binds a LoginViewModel and returns the DigitalUserResponse as JSON for AJAX callers.
// Keys match the C# property names; the microsite is fixed to "graberblinds" server-side.
// Always mints a fresh "login" score token. When the modal has shown an interactive challenge
// (low score), callers pass the solved challenge token in data.recaptchaChallenge and the server
// verifies it; otherwise the "no-challenge-needed" sentinel is sent and the server enforces the
// score threshold.
const login = (data, callbacks) => {
  const {
    username,
    password,
    rememberMe,
    recaptchaChallenge,
  } = data;
  const send = (recaptchaScoreToken) => {
    ajax({
      method: 'POST',
      url: window.loginUrl,
      body: JSON.stringify({
        username,
        password,
        rememberMe,
        recaptchaScoreToken: recaptchaScoreToken || '',
        recaptchaChallenge: recaptchaChallenge || 'no-challenge-needed',
      }),
      onSuccess: (responseData) => {
        if (callbacks && callbacks.onSuccess) {
          callbacks.onSuccess(responseData);
        }
      },
      onError: (request) => {
        if (callbacks && callbacks.onError) {
          callbacks.onError(JSON.parse(request.responseText));
        }
      },
    });
  };
  const api = window.grecaptchaApi;
  if (api && typeof api.mintScoreToken === 'function') {
    api.mintScoreToken('login')
      .then(send)
      .catch(() => send(''));
  } else {
    send('');
  }
};

const logout = (callbacks) => {
  ajax({
    method: 'POST',
    url: `${baseUrl}/logout`,
    contentType: null,
    parse: false,
    onSuccess: (responseData) => {
      if (callbacks && callbacks.onSuccess) {
        callbacks.onSuccess(responseData);
      }
    },
    onError: (request) => {
      if (callbacks && callbacks.onError) {
        callbacks.onError(request.responseText);
      }
    },
  });
};

const updatePassword = (data, callbacks) => {
  ajax({
    method: 'POST',
    url: `${apiBaseUrl}/password`,
    body: JSON.stringify(data),
    onSuccess: (responseData) => {
      if (callbacks.onSuccess) {
        callbacks.onSuccess(responseData);
      }
    },
    onError: () => {
      if (callbacks.onError) {
        callbacks.onError();
      }
    },
  });
};

// Favorite login modal create-account. Posts JSON to the CreateAccountPage URL, whose
// controller (CreateAccountPageController.Index, HttpPost) model-binds a CreateAccountViewModel
// and returns the DigitalUserResponse as JSON for AJAX callers. Keys match the C# property
// names (MVC's JSON model binder ignores the Newtonsoft [JsonProperty] kebab-case names); the
// microsite is fixed to "graberblinds" server-side.
const createConsumerAccount = (data, callbacks) => {
  ajax({
    method: 'POST',
    url: window.createAccountUrl,
    body: JSON.stringify(data),
    onSuccess: (responseData) => {
      if (callbacks.onSuccess) {
        callbacks.onSuccess(responseData);
      }
    },
    onError: (request) => {
      if (callbacks.onError) {
        callbacks.onError(JSON.parse(request.responseText));
      }
    },
  });
};

const hasAssets = (callback) => {
  ajax({
    method: 'GET',
    url: `${apiBaseUrl}/has-assets`,
    onSuccess: (responseData) => {
      if (callback) {
        callback(responseData);
      }
    },
    onError: () => {
      console.log('error');
    },
  });
};

export {
  addVisualization,
  addDealer,
  addSwatch,
  addSwatches,
  createConsumerAccount,
  getAccountData,
  getItemsByType,
  hasAssets,
  login,
  logout,
  updatePassword,
};
/* eslint-enable no-console */
